import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import AppContext from '@bibliocommons/context';

import { trackAnalyticsEvent } from 'app/actions/AnalyticsGa4Actions/actions';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import {
  eventNames,
  METADATA_CATEGORY_TYPE,
  UI_COMPONENT_TYPE,
  UI_CONTAINER_TYPE
} from 'app/constants/AnalyticsGa4Constants';
import { FILTER_PREFIX } from 'app/constants/BibConstants';
import {
  BROWSE_HOME_PAGE_PATH,
  BROWSE_SEARCH_PAGE_FACET_FIELD_IDS,
  FILTERABLE_DISPLAY_BIB_GRID_LIMIT
} from 'app/constants/BrowseConstants';
import { deserializeQuerystring, serializeQuerystring } from 'app/helpers/browse/searchPageQueryHelpers';
import { makeBackToLinkProps } from 'app/helpers/browse/search';
import { mapDisplayBibToAnalyticsBibEntity } from 'app/helpers/displayBib';
import { readSessionStorageItem } from 'app/helpers/storage';
import useBrowseFacets from 'app/hooks/useBrowseFacets';
import useIsomorphicLayoutEffect from 'app/hooks/useIsomorphicLayoutEffect';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import FilterableDisplayBibGrid from '../shared/FilterableDisplayBibGrid';
import BrowseSearchPage from './BrowseSearchPage';

const QUERY_STRING_KEY = {
  BACK_TO_PATH: 'returnToPath',
  BACK_TO_TITLE: 'returnToTitle',
  OVERRIDE_HEADING: 'title'
};

const BrowseSearchPageContainer = () => {
  const { setBackToLinkEnabled } = useContext(AppContext);
  const dispatch = useDispatch();
  const intl = useIntl();
  const location = useLocation();
  const { search } = location;
  const currentLibrary = useSelector(selectCurrentLibrary);

  const urlSearchParams = new URLSearchParams(search);
  const {
    query,
    searchType,
    [QUERY_STRING_KEY.BACK_TO_PATH]: backToCmsPath,
    [QUERY_STRING_KEY.BACK_TO_TITLE]: backToCmsTitle,
    [QUERY_STRING_KEY.OVERRIDE_HEADING]: overrideHeading
  } = Object.fromEntries(urlSearchParams);
  const baseQuery = { query, searchType };

  const baseApiParams = { limit: FILTERABLE_DISPLAY_BIB_GRID_LIMIT, ...baseQuery };

  const baseSearchParams = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (!key.startsWith(FILTER_PREFIX) && key !== 'page' && key !== 'searchScope') {
      baseSearchParams.append(key, value);
    }
  });

  const {
    displayAvailabilityByMetadataId,
    displayBibs,
    facetFields,
    handleFilterChange,
    isFetching,
    pagination,
    setShowMoreFieldId,
    unapplyAllFilters
  } = useBrowseFacets({
    baseApiParams,
    baseQuerystring: baseSearchParams.toString(),
    deserializeQuerystring,
    includedFieldIds: BROWSE_SEARCH_PAGE_FACET_FIELD_IDS,
    serializeQuerystring
  });

  const backToLinkHref = readSessionStorageItem('backToLink')?.href ?? '';
  const isFromBrowseHomePage = backToLinkHref === BROWSE_HOME_PAGE_PATH;
  useIsomorphicLayoutEffect(() => {
    setBackToLinkEnabled(true);
  }, [isFromBrowseHomePage, setBackToLinkEnabled]);

  const isBackToLinkEnabled = isFromBrowseHomePage || backToCmsPath;
  const backToLinkProps = isBackToLinkEnabled
    ? makeBackToLinkProps(intl.formatMessage, {
        backToCmsPath,
        backToCmsTitle,
        currentLibrary
      })
    : null;

  const appliedFiltersAccessibilityHeading = 'TODO: Accessibility Heading';
  const facetsHeading = intl.formatMessage({
    defaultMessage: 'Browse facets',
    id: 'browse_facets'
  });
  const heading = overrideHeading || intl.formatMessage({ id: 'browse' });

  const trackBibLinkClick = (displayBib, title, isImage, itemPosition) => {
    const { count: numTotalItems, page } = pagination?.toJS() ?? {};

    const { metadataId } = displayBib;
    const displayAvailability = displayAvailabilityByMetadataId[metadataId];

    dispatch(
      trackAnalyticsEvent({
        entities: {
          bib: mapDisplayBibToAnalyticsBibEntity(displayBib, displayAvailability),
          metadata: {
            metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
            metadata_value: title
          },
          ui: {
            ...(isImage ? {} : { ui_component_label: title }),
            ui_component_type: isImage ? UI_COMPONENT_TYPE.LINK_IMAGE : UI_COMPONENT_TYPE.LINK_TEXT,
            ui_container_type: UI_CONTAINER_TYPE.ITEM,
            ui_content_page: page,
            ui_content_position: itemPosition,
            ui_content_total_count: numTotalItems
          }
        },
        eventName: eventNames.CONTENT_CLICK
      })
    );
  };

  if (!facetFields) {
    return null;
  }
  return (
    <>
      <DocumentHeadContainer pageTitle={heading} />
      <BrowseSearchPage backToLinkProps={backToLinkProps} heading={heading}>
        <FilterableDisplayBibGrid
          appliedFiltersAccessibilityHeading={appliedFiltersAccessibilityHeading}
          displayBibs={displayBibs}
          facetFields={facetFields}
          facetsHeading={facetsHeading}
          handleFilterChange={handleFilterChange}
          hasBibRatings
          isFetching={isFetching}
          pagination={pagination}
          setShowMoreFieldId={setShowMoreFieldId}
          trackBibLinkClick={trackBibLinkClick}
          unapplyAllFilters={unapplyAllFilters}
        />
      </BrowseSearchPage>
    </>
  );
};

export default BrowseSearchPageContainer;
